import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Timer, Lock, CircleAlert, CheckCircle2 } from 'lucide-react';
import { useAccessToken } from '@/auth/useAccessToken';
import { useMe } from '@/features/me/useMe';
import { slaApi, type SlaTargetInput, type SlaUnit, type SlaPriority } from '@/features/sla/slaApi';
import { ApiError } from '@/lib/apiClient';

const PRIORITY_BLURB: Record<SlaPriority, string> = {
  Urgent: 'Production down / many users blocked.',
  High: 'Significant impact to one user or a team.',
  Normal: 'Standard request — the default.',
  Low: 'Minor / nice-to-have.'
};

/**
 * /settings/sla-targets — IT-Admin editing of the first-response SLA targets.
 * One row per priority: a number + unit (Hours / Business days). "Hours" is
 * wall-clock; "Business days" counts weekdays (Eastern), same as auto-close.
 * The Reports page reads these same targets for its SLA compliance metrics.
 */
export function AdminSlaTargets() {
  const me = useMe();
  const getAccessToken = useAccessToken();
  const queryClient = useQueryClient();
  const isAdmin = me.data?.role === 'ITAdmin';

  const targets = useQuery({
    queryKey: ['sla-targets'],
    queryFn: ({ signal }) => slaApi.get(getAccessToken, signal),
    enabled: isAdmin
  });

  // Local editable copy, seeded from the server response.
  const [draft, setDraft] = useState<SlaTargetInput[]>([]);
  useEffect(() => {
    if (targets.data) {
      setDraft(targets.data.targets.map((t) => ({ priority: t.priority, value: t.value, unit: t.unit })));
    }
  }, [targets.data]);

  const save = useMutation({
    mutationFn: () => slaApi.update(draft, getAccessToken),
    onSuccess: (res) => {
      queryClient.setQueryData(['sla-targets'], res);
      // Reports compliance depends on these — invalidate so it recomputes.
      void queryClient.invalidateQueries({ queryKey: ['metrics'] });
    }
  });

  const forbidden = (targets.error as ApiError)?.status === 403 || (me.data && !isAdmin);

  const dirty =
    targets.data != null &&
    draft.some((d, i) => {
      const o = targets.data!.targets[i];
      return !o || d.value !== o.value || d.unit !== o.unit;
    });

  const invalid = draft.some((d) => !(d.value > 0));

  const setRow = (priority: SlaPriority, patch: Partial<SlaTargetInput>) =>
    setDraft((prev) => prev.map((d) => (d.priority === priority ? { ...d, ...patch } : d)));

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Timer className="w-6 h-6 text-primary" /> SLA targets
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          First-response targets by priority. “Business days” counts weekdays (Eastern) — the same calendar as auto-close.
          The Reports page measures SLA compliance against these.
        </p>
      </header>

      {forbidden && (
        <div className="rounded-xl border border-dashed bg-card p-10 text-center">
          <Lock className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
          <div className="text-base font-medium">Admin access required</div>
          <p className="text-sm text-muted-foreground mt-1.5">Only IT Admins can edit SLA targets.</p>
        </div>
      )}

      {!forbidden && (
        <>
          {targets.isPending && <p className="text-sm text-muted-foreground">Loading…</p>}

          {targets.isError && !forbidden && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-start gap-2">
              <CircleAlert className="w-4 h-4 mt-0.5" />
              <span>Couldn't load SLA targets: {(targets.error as Error).message}</span>
            </div>
          )}

          {targets.data && (
            <div className="rounded-xl border bg-card divide-y divide-border">
              {draft.map((row) => (
                <div key={row.priority} className="flex flex-wrap items-center gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{row.priority}</div>
                    <div className="text-xs text-muted-foreground">{PRIORITY_BLURB[row.priority]}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Within</span>
                    <input
                      type="number"
                      min={1}
                      step={row.unit === 'Hours' ? 1 : 1}
                      value={row.value}
                      onChange={(e) => setRow(row.priority, { value: Number(e.target.value) })}
                      className="w-20 rounded-md border bg-background px-2 py-1.5 text-sm"
                    />
                    <select
                      value={row.unit}
                      onChange={(e) => setRow(row.priority, { unit: e.target.value as SlaUnit })}
                      className="rounded-md border bg-background px-2 py-1.5 text-sm"
                    >
                      <option value="Hours">hours</option>
                      <option value="BusinessDays">business days</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}

          {targets.data && (
            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                disabled={!dirty || invalid || save.isPending}
                onClick={() => save.mutate()}
                className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {save.isPending ? 'Saving…' : 'Save targets'}
              </button>
              {dirty && (
                <button
                  type="button"
                  disabled={save.isPending}
                  onClick={() => setDraft(targets.data!.targets.map((t) => ({ priority: t.priority, value: t.value, unit: t.unit })))}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Reset
                </button>
              )}
              {invalid && <span className="text-xs text-destructive">Each target must be at least 1.</span>}
              {save.isSuccess && !dirty && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Saved
                </span>
              )}
              {save.isError && <span className="text-xs text-destructive">{(save.error as Error).message}</span>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
